import { forwardRef, type ReactNode } from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowLeft, X } from 'lucide-react';

import { cn } from '../../lib/cn';
import { Z, type ZIndex } from '../../tokens/z-index';

export type SheetSide = 'bottom' | 'right' | 'responsive';

export interface SheetProps {
  /** Controlled open state. */
  open: boolean;
  /** Handler invoked when open state changes (Escape, backdrop, close button). */
  onOpenChange: (open: boolean) => void;
  /**
   * Side where the sheet slides in from. Default: `bottom` (mobile picker
   * pattern). `right` is suited for desktop drawers (filters, comments).
   * `responsive` is a bottom sheet below 768px and a 420px right drawer from
   * 768px up.
   */
  side?: SheetSide;
  /** Heading rendered inside DialogPrimitive.Title. Required for WCAG. */
  title: string;
  /** Optional description (sr-only if hidden visually). */
  description?: string;
  /** Body content. Scrolls when content exceeds the viewport cap. */
  children: ReactNode;
  /**
   * Sticky footer slot (e.g. Apply/Clear buttons). Rendered outside the
   * scrollable body so it stays visible even with long forms.
   */
  footer?: ReactNode;
  /** Extra classes on the Content container. */
  className?: string;
  /**
   * Visual drag handle on top of the sheet (ornamental — does not
   * drive drag-to-close). Default: `true` for bottom, `false` for right.
   */
  showHandle?: boolean;
  /** Hide the close button in the header. Default: `false` (shown). */
  hideCloseButton?: boolean;
  /**
   * Accessible label for the header close button. Pass a translated string
   * (e.g. `t('common:actions.close')`); defaults to the pt-BR primary locale.
   */
  closeLabel?: string;
  /**
   * Opt-in: when `true` and `side="bottom"`, the sheet is a bottom-anchored
   * `max-w-[480px]` column on mobile/tablet and becomes a vertically +
   * horizontally centered modal card (capped width, all-corners rounded) from
   * the `md` breakpoint (≥768px) up. Used by flows rendered inside a centered
   * narrow column (magic-link journey) so the sheet does not look glued to the
   * viewport footer on desktop while staying a bottom sheet on phones.
   * Ignored when `side="right"`. Default: `false`.
   */
  journeyColumn?: boolean;
  /**
   * Opt-in: em telas < 768px o sheet ocupa a viewport inteira (`100dvh`, sem
   * cantos arredondados) e o corpo deixa de rolar (`overflow-hidden`, sem
   * padding lateral/inferior), cedendo toda a altura útil ao filho. Serve a
   * conteúdo que administra o próprio scroll — caso canônico: iframe
   * cross-origin (assinatura eletrônica), onde um scroll externo por cima do
   * scroll interno faz o gesto de rolar competir com o de desenhar a
   * assinatura. A partir de `md` volta ao comportamento normal (altura por
   * conteúdo, corpo rolável, padding). Ignorado quando `side="right"`.
   * Default: `false`.
   */
  fillViewport?: boolean;
  /**
   * Mantém `fillViewport` também a partir de `md`. Útil quando a largura cruza
   * 768px porque um telefone foi girado: ele continua sendo uma tela baixa e
   * precisa da viewport inteira, não do layout de desktop por largura.
   * Ignorado sem `fillViewport` ou em `side="right"`.
   */
  fillViewportAtAllBreakpoints?: boolean;
  /** In full-viewport mode, place actions beside the body to preserve camera height. */
  footerPlacement?: 'bottom' | 'end';
  /** Rotate a full-viewport workspace without changing the device orientation. */
  viewportRotation?: 0 | 90;
  headerActions?: ReactNode;
  /**
   * Override do tier de z-index. Default: `Z.MODAL` (400). Usar
   * `Z.DROPDOWN_OVER_MODAL` (500) quando o Sheet abre por cima de outro
   * Dialog (ex: ChangeSetDiffSheet inline dentro de PlanEditModal).
   */
  zIndex?: ZIndex;
}

/**
 * Sheet — responsive drawer primitive built on `@radix-ui/react-dialog`.
 *
 * Variants:
 * - `side="bottom"` (default): slides up from the bottom, max-h 90dvh,
 *   rounded-t-xl, rounded handle bar visual. Mobile-first affordance.
 * - `side="right"`: slides in from the right edge, full-height (100dvh),
 *   w-full max-w-md. Desktop drawer pattern.
 *
 * Accessibility (via Radix Dialog): role="dialog" + aria-modal="true",
 * focus trap, Escape to close, click-outside to close, restore focus
 * on close, DialogTitle required for screen readers.
 *
 * No drag-to-close (see ADR-003 D1) — handle bar is ornamental.
 */
export const Sheet = forwardRef<HTMLDivElement, SheetProps>(function Sheet(
  {
    open,
    onOpenChange,
    side = 'bottom',
    title,
    description,
    children,
    footer,
    className,
    showHandle,
    hideCloseButton = false,
    closeLabel = 'Fechar',
    journeyColumn = false,
    fillViewport = false,
    fillViewportAtAllBreakpoints = false,
    footerPlacement = 'bottom',
    viewportRotation = 0,
    headerActions,
    zIndex = Z.MODAL,
  },
  ref,
) {
  const fillsViewport = fillViewport && side !== 'right';
  const fillsViewportAtAllSizes = fillsViewport && fillViewportAtAllBreakpoints;
  const sideFooter = fillsViewportAtAllSizes && footerPlacement === 'end' && !!footer;
  const rotated = fillsViewportAtAllSizes && viewportRotation === 90;
  const handleVisible = !fillsViewport && (showHandle ?? side !== 'right');

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          style={{ zIndex }}
          className={cn(
            // Pedro 2026-05-22: bumped 50→70 + blur-sm→md (mesmo do Dialog)
            'fixed inset-0 bg-secondary-950/70 backdrop-blur-md',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            'duration-200',
          )}
        />
        <DialogPrimitive.Content
          ref={ref}
          style={{ zIndex }}
          data-viewport-rotation={rotated ? '90' : '0'}
          className={cn(
            'fixed bg-white shadow-lg focus:outline-none flex flex-col',
            side === 'bottom' &&
              !journeyColumn && [
                'inset-x-0 bottom-0 rounded-t-xl max-h-[90dvh]',
                'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom',
                'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom',
              ],
            // journeyColumn: bottom-sheet on mobile, centered modal column on
            // desktop (≥768px). Below md it stays edge-to-edge anchored to the
            // bottom; at md+ it detaches from the bottom and self-centers as a
            // capped-width card (`inset-0 m-auto h-fit`, same approach as Dialog
            // — immune to transformed ancestors) so the magic-link journey sheet
            // does not look glued to the viewport footer on desktop.
            side === 'bottom' &&
              journeyColumn && [
                'inset-x-0 bottom-0 rounded-t-xl max-h-[90dvh] mx-auto w-full max-w-[480px]',
                'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom',
                'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom',
                'md:inset-0 md:m-auto md:h-fit',
                'md:w-[calc(100%-2rem)] md:max-h-[calc(100vh-2rem)] md:rounded-xl',
              ],
            side === 'right' && [
              'inset-y-0 right-0 h-[100dvh] w-full max-w-md',
              'data-[state=open]:animate-in data-[state=open]:slide-in-from-right',
              'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right',
            ],
            side === 'responsive' && [
              'inset-x-0 bottom-0 max-h-[86dvh] rounded-t-2xl',
              'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom',
              'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom',
              'md:inset-y-0 md:right-0 md:left-auto md:h-[100dvh] md:w-[420px] md:max-h-none md:rounded-none',
              'md:data-[state=open]:slide-in-from-right md:data-[state=closed]:slide-out-to-right',
            ],
            // Tela cheia abaixo de `md`: `dvh` (não `vh`) porque a barra de
            // endereço do Chrome mobile encolhe a viewport visível e `vh`
            // empurraria conteúdo para debaixo dela. Vem depois dos blocos
            // acima para que `tailwind-merge` resolva os conflitos de
            // altura/raio a favor destas classes.
            fillsViewport && [
              'h-[100dvh] max-h-[100dvh] rounded-none',
              journeyColumn
                ? 'md:h-fit md:max-h-[calc(100vh-2rem)] md:rounded-xl'
                : 'md:h-auto md:max-h-[90dvh] md:rounded-t-xl',
            ],
            // Um telefone deitado normalmente cruza o breakpoint `md` só pela
            // largura. Reaplica a viewport cheia depois das regras desktop para
            // a câmera não voltar a um bottom sheet baixo no iPhone/Android.
            fillsViewportAtAllSizes && [
              'inset-0 m-0 h-[100dvh] max-h-[100dvh] w-full max-w-none rounded-none',
              'md:inset-0 md:m-0 md:h-[100dvh] md:max-h-[100dvh] md:w-full md:max-w-none md:rounded-none',
            ],
            sideFooter &&
              'grid grid-cols-[minmax(0,1fr)_calc(7rem+var(--sheet-safe-right,env(safe-area-inset-right)))] grid-rows-[auto_minmax(0,1fr)]',
            rotated && [
              '!inset-auto !left-1/2 !top-1/2 !w-[100dvh] !h-[100dvw] !max-h-none',
              '!m-0 !max-w-none !rounded-none -translate-x-1/2 -translate-y-1/2 rotate-90 !animate-none',
              '[--sheet-safe-top:env(safe-area-inset-right)] [--sheet-safe-right:env(safe-area-inset-bottom)]',
              '[--sheet-safe-bottom:env(safe-area-inset-left)] [--sheet-safe-left:env(safe-area-inset-top)]',
            ],
            'duration-200',
            className,
          )}
        >
          {handleVisible ? (
            <div
              className={cn(
                'mx-auto mt-3 h-1 w-12 shrink-0 rounded-full bg-slate-300',
                // Drag-handle affordance is a bottom-sheet cue; hide it once the
                // journeyColumn variant becomes a centered modal on desktop.
                (journeyColumn || side === 'responsive') && 'md:hidden',
              )}
              aria-hidden
            />
          ) : null}

          <header
            className={cn(
              'flex items-start justify-between gap-4 px-4 pt-4 pb-3 shrink-0',
              headerActions && 'items-center',
              // Header compacto no modo tela cheia — continua acessível (o
              // associado precisa do botão fechar) gastando menos altura.
              fillsViewport && 'pt-2 pb-2 md:pt-4 md:pb-3',
              fillsViewportAtAllSizes && [
                'pt-1.5 pb-1.5 md:pt-1.5 md:pb-1.5',
                'pl-[calc(var(--sheet-safe-left,env(safe-area-inset-left))+0.75rem)]',
                'pr-[calc(var(--sheet-safe-right,env(safe-area-inset-right))+0.75rem)]',
              ],
              sideFooter &&
                'col-span-2 pt-[max(0.375rem,var(--sheet-safe-top,env(safe-area-inset-top)))]',
            )}
          >
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-base font-heading font-semibold text-secondary-950 truncate">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-0.5 text-sm text-slate-500">
                  {description}
                </DialogPrimitive.Description>
              ) : (
                // Visually hidden but accessible fallback — Radix Dialog
                // requires a Description for WCAG. Inline style is used
                // (instead of Tailwind `sr-only`) to avoid dependency on
                // Tailwind config edge cases that have duplicated the
                // title in prior renders.
                <DialogPrimitive.Description
                  style={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    borderWidth: 0,
                    // D-03: suppress browser-default black on sr-only element.
                    // Text is visually hidden; transparent avoids the color scan.
                    color: 'transparent',
                  }}
                >
                  {title}
                </DialogPrimitive.Description>
              )}
            </div>
            {headerActions}
            {!hideCloseButton ? (
              <DialogPrimitive.Close
                aria-label={closeLabel}
                className={cn(
                  'inline-flex size-11 items-center justify-center rounded-md',
                  'text-slate-500 hover:text-secondary-950 hover:bg-slate-100',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                )}
              >
                <X className="h-5 w-5" aria-hidden />
              </DialogPrimitive.Close>
            ) : null}
          </header>

          <div
            className={cn(
              'min-h-0 flex-1 overflow-y-auto px-4 pb-4 scrollbar-subtle',
              // Sem scroll externo e sem padding lateral/inferior: a altura
              // toda vai para o filho. `env(safe-area-inset-bottom)` mantém o
              // rodapé do conteúdo acima da home bar do iPhone.
              fillsViewport &&
                'overflow-hidden px-0 pb-[env(safe-area-inset-bottom)] md:overflow-y-auto md:px-4 md:pb-4',
              fillsViewportAtAllSizes &&
                'md:overflow-hidden md:px-0 md:pb-[var(--sheet-safe-bottom,env(safe-area-inset-bottom))]',
              rotated && 'pb-[var(--sheet-safe-bottom)]',
              sideFooter && 'min-w-0',
            )}
          >
            {children}
          </div>

          {footer ? (
            <footer
              className={cn(
                'sticky bottom-0 border-t border-slate-200 bg-white px-4 py-3 shrink-0',
                fillsViewportAtAllSizes && [
                  'py-2 md:py-2',
                  'pl-[calc(var(--sheet-safe-left,env(safe-area-inset-left))+0.75rem)]',
                  'pr-[calc(var(--sheet-safe-right,env(safe-area-inset-right))+0.75rem)]',
                  'pb-[max(0.5rem,var(--sheet-safe-bottom,env(safe-area-inset-bottom)))]',
                ],
                sideFooter &&
                  'static min-h-0 overflow-y-auto border-t-0 border-l pl-2 pr-[calc(0.5rem+var(--sheet-safe-right,env(safe-area-inset-right)))]',
              )}
            >
              {footer}
            </footer>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
});

/**
 * SheetBackButton — shared text-link back affordance for Sheet footers.
 *
 * Matches the secondary "Voltar" style from SheetWizard exactly (D-08 / sole-CTA pattern).
 * Use inside custom footer={<div className="flex flex-col gap-3"> <Button .../> <SheetBackButton .../> </div>}
 * to keep visual + interaction parity across DataSheet, VehicleSheet, SignatureSheet, Pagamento etc.
 *
 * Consumers supply translated label (e.g. t('common:actions.voltar')) for i18n.
 * Default label kept for generic (non-i18n) contexts.
 */
export interface SheetBackButtonProps {
  onClick: () => void;
  /** Translated label; defaults to Portuguese "Voltar" for legacy/generic use. */
  label?: string;
  className?: string;
  'data-testid'?: string;
}

export function SheetBackButton({
  onClick,
  label = 'Voltar',
  className,
  'data-testid': testId,
}: SheetBackButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center gap-1.5',
        'text-sm font-medium text-ink/60',
        'hover:text-ink/80 transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 rounded',
        className,
      )}
      data-testid={testId}
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {label}
    </button>
  );
}
